import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { useTenantAdminAuth } from '../../auth/TenantAdminAuthContext.jsx';

export function DashboardPage() {
  const { identity } = useTenantAdminAuth();
  const { data: menus, loading, error } = useApiQuery(() => api.get('/api/menus'), []);

  const publishedCount = menus?.filter((m) => m.isPublished).length ?? 0;
  const totalMenus = menus?.length ?? 0;
  const featuredMenu = menus?.find((m) => m.isPublished) ?? menus?.[0];
  const firstName = identity?.name?.trim()?.split(/\s+/)[0];

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <div>
          <h1>Welcome back{firstName ? `, ${firstName}` : ''}</h1>
          <p>Keep your digital menu accurate, inviting, and ready for guests.</p>
        </div>
        <Link to="/menus" className="btn btn-primary">Manage menus</Link>
      </div>

      {loading ? (
        <span className="spinner" aria-label="Loading" />
      ) : error ? (
        <p className="field-error">{error.message}</p>
      ) : (
        <>
          <div className="dashboard-summary panel">
            <div>
              <span className="dashboard-eyebrow">Your menu workspace</span>
              <h2>{totalMenus === 0 ? 'Start with your first menu' : featuredMenu?.name ?? 'Your menus'}</h2>
              <p>
                {totalMenus === 0
                  ? 'Create a menu and we’ll give you a ready-to-edit set of sections and dishes.'
                  : publishedCount > 0
                    ? 'Your latest published menu is live for customers. Keep it fresh as your menu changes.'
                    : 'Your menu is still a draft. Add your dishes, then publish when it is ready for guests.'}
              </p>
            </div>
            <Link to={featuredMenu ? `/menus/${featuredMenu.id}` : '/menus'} className="btn btn-primary">
              {totalMenus === 0 ? 'Create your first menu' : publishedCount > 0 ? 'Edit menu' : 'Continue editing'}
            </Link>
          </div>

          <div className="dashboard-stats" aria-label="Menu summary">
            <div className="dashboard-stat panel">
              <span className="dashboard-stat__label">Menus</span>
              <strong>{totalMenus}</strong>
              <span className="field-hint">Drafts and published menus</span>
            </div>
            <div className="dashboard-stat panel">
              <span className="dashboard-stat__label">Published</span>
              <strong>{publishedCount}</strong>
              <span className="field-hint">Visible to your customers</span>
            </div>
            <div className="dashboard-stat panel">
              <span className="dashboard-stat__label">Needs attention</span>
              <strong>{Math.max(totalMenus - publishedCount, 0)}</strong>
              <span className="field-hint">Menus still in draft</span>
            </div>
          </div>
        </>
      )}

      {!loading && !error && (
        <div className="dashboard-actions panel">
          <div>
            <h2>Get started</h2>
            <p>{totalMenus === 0 ? 'Create a menu, add your categories and dishes, then publish it for customers.' : 'Common tasks for keeping your menu ready to share.'}</p>
          </div>
          <div className="dashboard-actions__grid">
            <Link to="/menus" className="dashboard-action">
              <strong>{totalMenus === 0 ? 'Create a menu' : 'View all menus'}</strong>
              <span>{totalMenus === 0 ? 'Start from a ready-made template.' : 'Edit, publish, or archive menus.'}</span>
            </Link>
            {featuredMenu && (
              <Link to={`/menus/${featuredMenu.id}/appearance`} className="dashboard-action">
                <strong>Customize appearance</strong>
                <span>Adjust branding, colors, and layout.</span>
              </Link>
            )}
            {featuredMenu?.isPublished && (
              <Link to={`/menus/${featuredMenu.id}`} className="dashboard-action">
                <strong>Share your menu</strong>
                <span>Open the menu editor to download its QR code.</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
